import { PlaceSuggestion } from "@/components/others/place-autocomplete/types";
import { PLACE_DETAILS } from "@/gql/places";
import { useCurrentCityName } from "@/hooks/use-current-city";
import { useRouter } from "@/hooks/use-router-interceptor";
import { projectTypes } from "@/info/project-types";
import { logger } from "@/utils/logger";
import { encodeLocation } from "@/utils/search-url-transform/encode-decode-location";
import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useState, useTransition } from "react";
const isProduction = process.env.NODE_ENV === "production";
export const useSearchNavigation = () => {
  const router = useRouter();
  const currentCity = useCurrentCityName();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isPending, startTransition] = useTransition();
  const [isNavigating, setIsNavigating] = useState(false);

  // Apollo query for fetching place details by ID
  const [fetchPlaceDetails] = useLazyQuery(PLACE_DETAILS, {
    fetchPolicy: isProduction ? "cache-first" : "network-only",
    errorPolicy: "all"
  });
  useEffect(() => {
    if (currentCity) {
      router.prefetch(`/${currentCity}`);
    }
  }, [currentCity, router]);
  const handleConfirmGeneralSearch = useCallback(() => {
    setShowConfirmDialog(false);
    setIsNavigating(true);
    startTransition(() => {
      // Navigate to city search page for all users
      logger.info(`Navigating to city search: ${currentCity}`);
      router.push(`/${currentCity}`);
    });
  }, [router, currentCity]);
  const handleCancelDialog = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  // Text-based search handler (primary method)
  const handleSearchWithFallback = useCallback(async (suggestion: PlaceSuggestion) => {
    // Prevent multiple rapid clicks
    if (isNavigating || isPending) {
      return;
    }
    const {
      mainText: locationText,
      placeId
    } = suggestion;
    setIsNavigating(true);
    try {
      // Handle user-location-* format (synthetic placeId from browser geolocation)
      // No need to call Apollo - we already have the coordinates
      const userLocationMatch = placeId.match(/^user-location-(-?\d+\.?\d*)-(-?\d+\.?\d*)$/);
      if (userLocationMatch) {
        const lat = parseFloat(userLocationMatch[1]);
        const long = parseFloat(userLocationMatch[2]);
        if (!isNaN(lat) && !isNaN(long)) {
          logger.info({
            lat,
            long
          }, "Using browser geolocation coordinates directly");
          const route = `/${currentCity}?center=${lat}-${long}`;
          router.prefetch(route);
          router.push(route);
          return;
        }
      }

      // First fetch place details to get the types using Apollo
      const {
        data
      } = await fetchPlaceDetails({
        variables: {
          params: {
            placeDetails: {
              placeId: placeId
            }
          }
        }
      });
      const placeDetails = data?.locations?.placeDetails;
      const types = placeDetails?.types || [];
      startTransition(() => {
        // Use location text to create route structure with proper encoding
        let localitySlug = encodeLocation(locationText);

        // Detect if it's a project based on types from GraphQL API
        const isProject = types.some((type: string) => projectTypes.includes(type));
        if (isProject) {
          localitySlug += "_project";
        }

        // Use path-based format with in- prefix (same as property filters)
        const route = `/${currentCity}/in-${localitySlug}`;
        logger.info({
          locationText,
          localitySlug,
          currentCity,
          route,
          types,
          isProject
        }, `Navigating with location text to /${currentCity}`);

        // Use router.push with prefetch for faster navigation
        router.prefetch(route);
        router.push(route);
      });
    } catch (_error) {
      // Fallback to basic navigation without project detection
      startTransition(() => {
        const localitySlug = encodeLocation(locationText);
        const route = `/${currentCity}/in-${localitySlug}`;
        router.prefetch(route);
        router.push(route);
      });
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [isNavigating, isPending, router, currentCity]);
  const handleSearchWithCoordinates = useCallback(async ({
    lat,
    long
  }: {
    lat: number;
    long: number;
  }) => {
    const route = `/bengaluru?center=${lat}-${long}`;
    router.push(route);
  }, [router]);
  const isLoading = isNavigating || isPending;
  return {
    showConfirmDialog,
    setShowConfirmDialog,
    isLoading,
    isPending,
    setIsNavigating,
    handleConfirmGeneralSearch,
    handleCancelDialog,
    handleSearchWithFallback,
    handleSearchWithCoordinates
  };
};